export type CoachVeeraPanelHintValue =
  | string
  | number
  | boolean
  | null
  | readonly CoachVeeraPanelHintValue[]
  | { readonly [key: string]: CoachVeeraPanelHintValue };

export interface CoachVeeraPanelLaunchData {
  readonly selectedLeaderIds?: readonly string[];
  readonly [key: string]: CoachVeeraPanelHintValue | undefined;
}

/**
 * Display-only launch treatment. This must not be copied into `data`, which is
 * passed to the existing coach-chat request context.
 */
export type CoachVeeraPanelPresentation =
  | { readonly kind: "portfolio" }
  | { readonly kind: "leader_context"; readonly leaderName: string };

export interface CoachVeeraPanelLaunchContext {
  readonly pageName: string;
  readonly source: string;
  readonly presentation?: CoachVeeraPanelPresentation;
  readonly leaderId?: string;
  readonly relationshipRevision?: string;
  readonly accessGeneration?: string;
  readonly data?: CoachVeeraPanelLaunchData;
}

export interface CoachVeeraPanelSnapshot {
  readonly open: boolean;
  readonly hasOpened: boolean;
  readonly context: CoachVeeraPanelLaunchContext | null;
}

type CoachVeeraPanelSubscriber = () => void;
type CoachVeeraPanelInvoker = Pick<HTMLElement, "focus">;

const CLOSED_SNAPSHOT: CoachVeeraPanelSnapshot = {
  open: false,
  hasOpened: false,
  context: null,
};

let snapshot = CLOSED_SNAPSHOT;
let launchFocusTarget: CoachVeeraPanelInvoker | null = null;
let pendingFocusReturnTarget: CoachVeeraPanelInvoker | null = null;
const subscribers = new Set<CoachVeeraPanelSubscriber>();

const cloneHintValue = (
  value: CoachVeeraPanelHintValue,
): CoachVeeraPanelHintValue => {
  if (Array.isArray(value)) {
    return value.map(cloneHintValue);
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        cloneHintValue(nestedValue),
      ]),
    );
  }
  return value;
};

const cloneLaunchData = (
  data: CoachVeeraPanelLaunchData,
): CoachVeeraPanelLaunchData => cloneHintValue(data) as CoachVeeraPanelLaunchData;

const clonePresentation = (
  presentation: CoachVeeraPanelPresentation,
): CoachVeeraPanelPresentation => presentation.kind === "leader_context"
  ? { kind: "leader_context", leaderName: presentation.leaderName }
  : { kind: "portfolio" };

const publish = (nextSnapshot: CoachVeeraPanelSnapshot) => {
  snapshot = nextSnapshot;
  subscribers.forEach((subscriber) => subscriber());
};

export const getCoachVeeraPanelSnapshot = (): CoachVeeraPanelSnapshot => snapshot;

export const subscribeToCoachVeeraPanel = (
  subscriber: CoachVeeraPanelSubscriber,
): (() => void) => {
  subscribers.add(subscriber);
  return () => subscribers.delete(subscriber);
};

export const openCoachVeeraPanel = (
  context: CoachVeeraPanelLaunchContext,
  invoker?: CoachVeeraPanelInvoker | null,
) => {
  launchFocusTarget = invoker ?? null;
  pendingFocusReturnTarget = null;
  publish({
    open: true,
    hasOpened: true,
    context: {
      ...context,
      presentation: context.presentation ? clonePresentation(context.presentation) : undefined,
      data: context.data ? cloneLaunchData(context.data) : undefined,
    },
  });
};

export const closeCoachVeeraPanel = () => {
  if (!snapshot.open) return;
  pendingFocusReturnTarget = launchFocusTarget;
  launchFocusTarget = null;
  publish({ ...snapshot, open: false });
};

export const clearCoachVeeraPanelContext = () => {
  if (!snapshot.context) return;
  launchFocusTarget = null;
  pendingFocusReturnTarget = null;
  publish({ ...snapshot, context: null });
};

export const restoreCoachVeeraPanelFocus = () => {
  const target = pendingFocusReturnTarget;
  pendingFocusReturnTarget = null;
  target?.focus();
};

export const resetCoachVeeraPanelBoundary = () => {
  launchFocusTarget = null;
  pendingFocusReturnTarget = null;
  publish(CLOSED_SNAPSHOT);
};

export const resetCoachVeeraPanelStoreForTests = () => {
  snapshot = CLOSED_SNAPSHOT;
  launchFocusTarget = null;
  pendingFocusReturnTarget = null;
  subscribers.clear();
};
